import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import FieldWrap from '../components/form/FieldWrap';
import Input from '../components/form/Input';
import Validation from '../components/form/Validation';
import Icon from '../components/icon/Icon';
import PageWrapper from '../components/layouts/PageWrapper/PageWrapper';
import Button from '../components/ui/Button';
import { authPages } from '../config/pages.config';
import { useAuth } from '../context/authContext';
import { resetPassword } from '../services/apiService';
import LogoTemplate from '../templates/layouts/Logo/Logo.template';
import theRapixLOgoSmall from '../assets/Logo_small.svg';

export type TValues = {
	password: string;
	confirmPassword: string;
};

const validationSchema = yup.object({
	password: yup.string().min(6, 'Password must be at least 6 characters').required('Required'),
	confirmPassword: yup
		.string()
		.oneOf([yup.ref('password'), undefined], 'Passwords and confirm password must match')
		.required('Required'),
});

const ResetPasswordPage = () => {
	const navigate = useNavigate();
	const { loading, call } = resetPassword();
	const { onLogout } = useAuth();
	const { token } = useParams<{ token: string }>();
	const [passwordShowStatus, setPasswordShowStatus] = useState<boolean>(false);
	const [confirmPasswordShowStatus, setConfirmPasswordShowStatus] = useState<boolean>(false);

	const formik = useFormik({
		initialValues: {
			password: '',
			confirmPassword: '',
		},
		validationSchema,
		onSubmit: (values: TValues, { setFieldError }) => {
			call(values.password, token ?? '').subscribe({
				next: (res: any) => {
					formik.resetForm();
					toast.success(res.msg);
					navigate(authPages.loginPage.to);
				},
				error: (e: any) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.data?.msg || e.message);
				},
			});
		},
	});

	return (
		<PageWrapper
			isBeforeLoginRoute={true}
			isProtectedRoute={false}
			className='bg-white dark:bg-inherit'
			name='Reset Password'>
			<div className='container mx-auto flex h-full items-center justify-center'>
				<div className='flex max-w-md flex-col gap-8'>
					<div className='logo-small'>
						<img src={theRapixLOgoSmall} alt='THERapix' className='home-logo' />
					</div>
					<div>
						<span className='text-4xl font-semibold'>Reset Password</span>
					</div>
					<div>
						<span>Reset password by entering your new password</span>
					</div>
					<form className='flex flex-col gap-4' noValidate>
						<div>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.password}
								invalidFeedback={formik.errors.password}>
								<FieldWrap
									firstSuffix={<Icon icon='HeroKey' className='mx-2' />}
									lastSuffix={
										<Icon
											className='mx-2 cursor-pointer'
											icon={passwordShowStatus ? 'HeroEyeSlash' : 'HeroEye'}
											onClick={() => {
												setPasswordShowStatus(!passwordShowStatus);
											}}
										/>
									}>
									<Input
										dimension='lg'
										type={passwordShowStatus ? 'text' : 'password'}
										autoComplete='current-password'
										id='password'
										name='password'
										placeholder='Password'
										value={formik.values.password}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Validation
								isValid={formik.isValid}
								isTouched={formik.touched.confirmPassword}
								invalidFeedback={formik.errors.confirmPassword}>
								<FieldWrap
									firstSuffix={<Icon icon='HeroLockClosed' className='mx-2' />}
									lastSuffix={
										<Icon
											className='mx-2 cursor-pointer'
											icon={
												confirmPasswordShowStatus
													? 'HeroEyeSlash'
													: 'HeroEye'
											}
											onClick={() => {
												setConfirmPasswordShowStatus(
													!confirmPasswordShowStatus,
												);
											}}
										/>
									}>
									<Input
										dimension='lg'
										type={confirmPasswordShowStatus ? 'text' : 'password'}
										autoComplete='current-password'
										id='confirmPassword'
										name='confirmPassword'
										placeholder='Confirm Password'
										value={formik.values.confirmPassword}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FieldWrap>
							</Validation>
						</div>
						<div>
							<Button
								isLoading={loading}
								size='lg'
								variant='solid'
								className='w-full font-semibold'
								onClick={() => formik.handleSubmit()}>
								Reset Password
							</Button>
						</div>
					</form>
				</div>
			</div>
		</PageWrapper>
	);
};

export default ResetPasswordPage;
